/**
 * What to be told about, and how, in the bell beside "notify me on this
 * computer" -- where somebody deciding how they hear about their work will
 * look.
 *
 *   - Replies on my tickets (089): somebody else writing on a ticket
 *     assigned to you. One setting for every desk, and it says so. The
 *     browser is on unless switched off.
 *   - Every new ticket on this desk (088, email 090). Per desk, off unless
 *     switched on.
 *
 * Each can go to the browser, to email, or both. A browser notification
 * reaches only browsers where they are allowed (the panel below); email
 * needs nothing, so these show whether or not this browser is set up.
 */
import { MessageSquare, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';
import { Switch, useToast } from '../ui/index.js';

interface Prefs {
  push: boolean;
  email: boolean;
}

function usePrefs(key: readonly unknown[], url: string, enabled = true) {
  const toast = useToast();
  const qc = useQueryClient();
  const prefs = useQuery({ queryKey: key, queryFn: () => api.get<Prefs>(url), enabled });
  const save = useMutation({
    mutationFn: (next: Prefs) => api.put<Prefs>(url, next),
    onSuccess: (r) => qc.setQueryData(key, { push: r.push, email: r.email }),
    onError: (e) => toast.error('Not saved', e instanceof ApiError ? e.message : undefined),
  });
  return { prefs: prefs.data, busy: save.isPending, set: (next: Prefs) => save.mutate(next) };
}

function Section({ icon, title, prefs, busy, set }: { icon: ReactNode; title: string } & ReturnType<typeof usePrefs>) {
  if (!prefs) return null;
  return (
    <div className="space-y-2 border-t border-line-subtle px-3 py-2.5 text-2xs">
      <div className="flex items-start gap-2">
        {icon}
        <p className="min-w-0 flex-1 text-ink-secondary">{title}</p>
      </div>
      <Switch
        checked={prefs.push}
        disabled={busy}
        onCheckedChange={(push) => set({ ...prefs, push })}
        label="Browser notification"
      />
      <Switch checked={prefs.email} disabled={busy} onCheckedChange={(email) => set({ ...prefs, email })} label="Email" />
    </div>
  );
}

const iconClass = 'mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-tertiary';

export function NotifySettings({ tenant }: { tenant: string }) {
  const replies = usePrefs(['reply-notifications'], '/api/v1/me/reply-notifications');
  const everyNew = usePrefs(
    ['new-ticket-notifications', tenant],
    `/api/v1/tenants/${tenant}/me/new-ticket-notifications`,
    Boolean(tenant),
  );
  return (
    <>
      <Section
        icon={<MessageSquare aria-hidden className={iconClass} />}
        title="When somebody else writes on a ticket assigned to you, on any desk:"
        {...replies}
      />
      <Section
        icon={<Inbox aria-hidden className={iconClass} />}
        title="Every new ticket on this desk:"
        {...everyNew}
      />
    </>
  );
}
